import React, { useEffect } from 'react';
import { useMediaQuery } from 'react-responsive';
import cN from 'classnames';
import sanitize from 'sanitize-html';
import parse from 'html-react-parser';
import { ICollapseListProps } from '@lion-design/core/lib/src/CollapseList';
import {
    SubTitle,
    Table,
    ItineraryDaily,
    ItineraryDailyLightbox,
    ItineraryMarketing,
    ItineraryNoticesFrame,
    ItineryTravelInfoM,
    ItineryTravelInfoPC,
    ItinerarySelfGuided,
} from '@components/DetailPage';
import { I_CustomTableProps, TableContent } from '@components/DetailPage/Table';
import FeatureContent from '@components/Common/FeatureContent';
import { useSelector, useDispatch } from 'react-redux';
import { setItineraries, setOptionalInfo, setNoticeinfo } from '@Redux/detail/Itineraries/actions';
import { I_Group, I_StationInfoList } from '@Redux/detail/Group/interfaces';
import { I_OptionalInfo } from '@Redux/detail/Itineraries/interfaces';
import { RootState, store } from '@Redux/detail/store';

const Itinerary = () => {
    const dispatch = useDispatch();
    const { StationInfoList } = useSelector((store: RootState) => store.stationInfoReducer);
    const {
        Features,
        OptionalInfoList,
        SelfSelectedList,
        Remark,
        LeaveTeamReg,
        SafeReg,
        ItinnotesReg,
        ActiveNote,
        EachTripList,
        NoteList,
        DailyList,
        BankContent,
        AntiepReg,
        MarketingContent,
    } = useSelector((store: RootState) => store.itinerariesReducer);
    const recommends = useSelector((store: RootState) => store.recommendReducer);
    const isPc = useMediaQuery({ query: '(min-width: 980px)' });

    const { travelinfo } = document.getElementById('detailPagePara')!.dataset!;
    const { GroupInfo, IsPreview }: I_Group = JSON.parse(travelinfo!);
    const { GroupID, TourSource, TourID, TravelType } = GroupInfo;

    useEffect(() => {
        const reqBody = {
            GroupID,
            TourSource,
        };
        dispatch(setOptionalInfo(reqBody));
        dispatch(setNoticeinfo(reqBody));
    }, [dispatch, GroupID, TourSource]);

    useEffect(() => {
        const itinerariesReqbody = {
            TourID,
            IsPreview,
            TravelType,
            TourSource,
        };
        dispatch(setItineraries(itinerariesReqbody));
    }, [dispatch, TourID, TravelType, IsPreview, TourSource]);

    //集合地點
    const placeTitles: ICollapseListProps['titles'] = [
        {
            name: '集合地點',
            key: 'Place',
            size: '33.333%',
        },
        {
            name: '地址',
            key: 'Address',
            size: '33.333%',
        },
        {
            name: '集合時間',
            key: 'MusterTime',
            size: '33.333%',
        },
    ];
    const placeData = StationInfoList.map((StationInfoList: I_StationInfoList) => {
        const { Place, MusterTime, Address } = StationInfoList;
        return {
            Place: <TableContent title="集合地點" content={Place} />,
            MusterTime: <TableContent title="集合時間" content={MusterTime} />,
            Address: <TableContent title="地址" content={Address} />,
        };
    });
    //自費行程
    const activityTitles: ICollapseListProps['titles'] = [
        {
            name: '城市',
            key: 'CityName',
            size: '13%',
        },
        {
            name: '節目名稱',
            key: 'ProgramName',
            size: '13%',
        },
        {
            name: '簡介',
            key: 'Summary',
            size: '18%',
        },
        {
            name: '費用',
            key: 'Cost',
            size: '13%',
        },
        {
            name: '年齡說明',
            key: 'AgeDesc',
            size: '9%',
        },
        {
            name: '備註',
            key: 'Remarks',
            size: '27%',
        },
        {
            name: '危險',
            key: 'DangerLevel',
            size: '7%',
        },
    ];
    const activityData = OptionalInfoList.map((OptionalInfoList: I_OptionalInfo) => {
        const {
            CityName,
            ProgramName,
            Summary,
            CurrID,
            CurrName,
            AdultsCost,
            ChildCost,
            AgeDesc,
            Remarks,
            DangerLevel,
        } = OptionalInfoList;

        const adultCost = (CurrID || '') + ' ' + AdultsCost.toLocaleString();
        const childCost = (CurrID || '') + ' ' + ChildCost.toLocaleString();
        const Cost = `${CurrName || ''} <div> 大人 ${adultCost} </div>
            ${ChildCost > 0 ? `<div>小孩 ${childCost}</div>` : ''}`;

        return {
            CityName: <TableContent title="城市" content={CityName} />,
            ProgramName: <TableContent title="節目名稱" content={ProgramName} />,
            Summary: <TableContent title="簡介" content={Summary} />,
            Cost: <TableContent title="費用" content={Cost} />,
            AgeDesc: <TableContent title="年齡說明" content={AgeDesc} />,
            Remarks: <TableContent title="備註" content={Remarks} />,
            DangerLevel: <TableContent title="危險" content={DangerLevel} />,
        };
    });
    // 自選行程
    const selfSelectedTitles: I_CustomTableProps['titles'] = [
        {
            name: '日期',
            key: 'ItineraryDate',
            size: '11%',
        },
        {
            name: '國家城市',
            key: 'PlaceName',
            size: '11%',
        },
        {
            name: '行程名稱',
            key: 'ItineraryName',
            size: '14%',
        },
        {
            name: '說明',
            key: 'Illustrate',
            size: '28%',
        },
        {
            name: '費用',
            key: 'PriceList',
            size: '14%',
        },
        {
            name: '預估時間',
            key: 'CostTime',
            size: '11%',
        },
        {
            name: '行程編號',
            key: 'ItineraryNum',
            size: '11%',
        },
    ];

    const selfSelectedSort: JSX.Element[] = [];
    let lastGroupType: string | null = null;

    const createSubHead = (key: string, subRowTitle: string) => {
        return (
            <span key={`group-${key}`} className="subRow">
                {subRowTitle}
            </span>
        );
    };

    const formatPriceList = (priceList: { Title: string; Currency: string; Price: string }[]) =>
        priceList
            .map((price) => `<div>${price.Title}：${price.Currency} ${price.Price}</div>`)
            .join('');

    const formatCostTime = (time: string) => `${time} hrs`;
    const formatIllustrate = (illustrate: string) => `<pre>${illustrate}</pre>`;

    SelfSelectedList.forEach((item, index) => {
        const { CostTime, Illustrate, PriceList, GroupType, GroupTypeName } = item;

        const isMultiSelect = GroupType.length > 0;

        // 是 多選 但不同 GroupType -> 新增一行 多選 標題
        if (isMultiSelect && lastGroupType !== GroupType) {
            selfSelectedSort.push(createSubHead(`group-${GroupType}`, GroupTypeName));
            lastGroupType = GroupType;
        } else if (!isMultiSelect) {
            // 是 單選 -> 新增一行 單選 標題
            selfSelectedSort.push(createSubHead(`group-${index}`, GroupTypeName));
            lastGroupType = null;
        }

        const tableContent = selfSelectedTitles.map((title) => {
            const { name, key, size } = title;
            let content: string;

            switch (key) {
                case 'PriceList':
                    content = formatPriceList(PriceList);
                    break;
                case 'Illustrate':
                    content = formatIllustrate(Illustrate);
                    break;
                case 'CostTime':
                    content = formatCostTime(CostTime);
                    break;
                default:
                    content = item[key as keyof typeof item] as string;
            }

            return (
                <TableContent title={name} key={key} content={content} customTableColSize={size} />
            );
        });
        selfSelectedSort.push(<> {tableContent}</>);
    });

    // 行程特殊提醒
    const noticeContent =
        Remark?.length > 0 &&
        parse(sanitize(Remark, { allowedTags: false, allowedAttributes: false }));
    // 行程備註
    const itineraryNote = ItinnotesReg?.length > 0 && parse(ItinnotesReg);
    // 安全守則
    const safetyContent = SafeReg?.length > 0 && parse(SafeReg);
    // 團體航班規定事項
    const leaveTeamContent = LeaveTeamReg?.length > 0 && parse(LeaveTeamReg);
    // 活動附註
    const activeNoteContent = ActiveNote?.length > 0 && parse(ActiveNote);
    // 防疫說明
    const antiepRegContent = AntiepReg?.length > 0 && parse(AntiepReg);

    const scrollOffset = isPc ? -70 : -50;

    return (
        <div
            className={cN('itinerary_wrapper', 'Container', {
                noRecommend: recommends?.recommendList?.length === 0,
            })}
        >
            <FeatureContent features={Features} />
            {MarketingContent?.ImgPC && <ItineraryMarketing MarketingContent={MarketingContent} />}
            {BankContent && (
                <div
                    className="itinerary_bankContent"
                    dangerouslySetInnerHTML={{
                        __html: sanitize(BankContent, {
                            allowedTags: false,
                            allowedAttributes: false,
                        }),
                    }}
                />
            )}
            {StationInfoList.length > 0 && (
                <div className={cN('itinerary_table', 'place')} id="assemble">
                    <SubTitle texts={{ main: '集合地點', sub: 'Assembling Place' }} />
                    <Table titles={placeTitles} dataSource={placeData} />
                    <div className="itinerary_note">
                        集合時間僅為參考用，實際時間請以當團人員通知時間為準
                    </div>
                </div>
            )}
            {SelfSelectedList.length > 0 && (
                <div className={cN('itinerary_table', 'optional')} id="optional">
                    <SubTitle texts={{ main: '自選行程', sub: 'Optional itinerary' }} />
                    <Table titles={selfSelectedTitles} tableElement={selfSelectedSort} />
                    <div className="itinerary_note">※行程時間僅供參考，實際依當日狀況調整為準</div>
                </div>
            )}
            {DailyList.length > 0 && (
                <>
                    <div className="itinerary" id="schedules">
                        <SubTitle texts={{ main: '每日行程', sub: 'Daily Itinerary' }} />
                        <ItineraryDaily />
                    </div>
                    <ItineraryDailyLightbox />
                </>
            )}
            {OptionalInfoList.length > 0 && (
                <div className={cN('itinerary_table', 'activity')} id="activities">
                    <SubTitle texts={{ main: '自費活動', sub: 'Activities' }} />
                    <Table titles={activityTitles} dataSource={activityData} />
                    <div className="itinerary_note">
                        自費活動項目以實際出發日為主。
                        <span>危險等級： A危險 B一些危險 C安全</span>
                    </div>
                </div>
            )}
            {EachTripList.length > 0 && (
                <div className="itinerary_self">
                    <SubTitle texts={{ main: '產品內容', sub: 'Contents' }} />
                    <ItinerarySelfGuided />
                </div>
            )}

            {!!AntiepReg && (
                <div className="itinerary_notice" id="EpidemicPrevention">
                    <ItineraryNoticesFrame
                        texts={{ main: '防疫規範', sub: 'Epidemic Prevention Norms' }}
                        scrollEleId="EpidemicPrevention"
                        scrollOffset={scrollOffset}
                    >
                        {antiepRegContent}
                    </ItineraryNoticesFrame>
                </div>
            )}

            {!!noticeContent && (
                <div className="itinerary_notice no_collapse" id="notes">
                    <SubTitle texts={{ main: '行程特殊提醒', sub: 'Itinerary Remarks' }} />
                    <div className="content">{noticeContent}</div>
                </div>
            )}

            {!!itineraryNote && (
                <div className="itinerary_notice" id="itineraryNote">
                    <ItineraryNoticesFrame
                        texts={{ main: '行程備註', sub: 'Itinerary Notes' }}
                        scrollEleId="itineraryNote"
                    >
                        {itineraryNote}
                    </ItineraryNoticesFrame>
                </div>
            )}

            {!!activeNoteContent && (
                <div className="itinerary_notice" id="activeNote">
                    <ItineraryNoticesFrame
                        texts={{ main: '活動附註', sub: 'Activity Notes' }}
                        scrollEleId="activeNote"
                        scrollOffset={scrollOffset}
                    >
                        {activeNoteContent}
                    </ItineraryNoticesFrame>
                </div>
            )}
            {!!safetyContent && (
                <div className="itinerary_notice" id="safety">
                    <ItineraryNoticesFrame
                        texts={{ main: '安全守則', sub: 'Safety Rules' }}
                        scrollEleId="safety"
                        scrollOffset={scrollOffset}
                    >
                        {safetyContent}
                    </ItineraryNoticesFrame>
                </div>
            )}
            {!!leaveTeamContent && (
                <div className="itinerary_notice" id="leaveTeam">
                    <ItineraryNoticesFrame
                        texts={{ main: '團體航班規定事項', sub: 'Group Flight Rules' }}
                        scrollEleId="leaveTeam"
                        scrollOffset={scrollOffset}
                    >
                        {leaveTeamContent}
                    </ItineraryNoticesFrame>
                </div>
            )}
            {NoteList.length > 0 && (
                <div className="itinerary_infos" id="notices">
                    <SubTitle texts={{ main: '旅遊資訊', sub: 'Travel Information' }} />
                    <div className="itinerary_infos_pc">
                        <ItineryTravelInfoPC />
                    </div>
                    <div className="itinerary_infos_m">
                        <ItineryTravelInfoM />
                    </div>
                </div>
            )}
        </div>
    );
};

export default Itinerary;
